import React from 'react';
import moment from 'moment';

import backend from 'utils/backend';

import {addUsage, getMessageText} from './chatUtils';

const STREAM_RENDER_INTERVAL_MS = 100;
// The done frame follows a stop within a second; without it by then, the stream has already ended.
const STOP_FALLBACK_MS = 5000;

// One agent turn over the chat WebSocket: the answer text and token usage while it streams, then the finished message.
const useAgentChatTurn = ({onInit, onDone, onError, onClose, onStopFallback}) => {
  const [loading, setLoading] = React.useState(false);
  const [streamingText, setStreamingText] = React.useState('');
  const [liveUsage, setLiveUsage] = React.useState(null);
  const streamingTextRef = React.useRef('');
  const liveUsageRef = React.useRef(null);
  const renderTimerRef = React.useRef(null);
  // The turn the agent is answering, known from the init frame; stop sends it.
  const [activeRun, setActiveRun] = React.useState(null);
  const activeRunRef = React.useRef(null);
  const [stopping, setStopping] = React.useState(false);
  const wsRef = React.useRef(null);
  const stopFallbackTimerRef = React.useRef(null);
  // The latest callbacks, so send and stop stay stable across renders.
  const callbacksRef = React.useRef();
  callbacksRef.current = {onInit, onDone, onError, onClose, onStopFallback};

  const endTurn = React.useCallback(() => {
    clearTimeout(renderTimerRef.current);
    renderTimerRef.current = null;
    clearTimeout(stopFallbackTimerRef.current);
    stopFallbackTimerRef.current = null;
    streamingTextRef.current = '';
    liveUsageRef.current = null;
    activeRunRef.current = null;
    wsRef.current = null;
    setStreamingText('');
    setLiveUsage(null);
    setActiveRun(null);
    setStopping(false);
    setLoading(false);
  }, []);

  React.useEffect(() => () => {
    clearTimeout(renderTimerRef.current);
    clearTimeout(stopFallbackTimerRef.current);
  }, []);

  const send = React.useCallback(async (message, sessionRn, newSessionOptions = {}) => {
    endTurn();
    setLoading(true);

    const ws = new backend.agentChat.AgentChatWebsocketManager({
      onInit: (data) => {
        // Hudson answers in one step, so its turns have no Stop.
        if (data.session.agentTarget !== 'EVENT_ROUTER_DISCIPLINE') {
          activeRunRef.current = {sessionRn: data.session.sessionRn, runRn: data.userMessage.messageRn};
          setActiveRun(activeRunRef.current);
        }
        callbacksRef.current.onInit?.(data);
      },
      onChunk: (data) => {
        streamingTextRef.current += data.chunk;
        if (renderTimerRef.current) return;
        // Chunks arrive many times a second, so the live answer renders at most once per interval.
        renderTimerRef.current = setTimeout(() => {
          renderTimerRef.current = null;
          setStreamingText(streamingTextRef.current);
        }, STREAM_RENDER_INTERVAL_MS);
      },
      onUsage: (data) => {
        if (!data.usage) return;
        if (data.scope === 'TURN') {
          liveUsageRef.current = data.usage;
        } else if (data.scope === 'MODEL_CALL') {
          liveUsageRef.current = addUsage(liveUsageRef.current, data.usage);
        } else {
          return;
        }
        setLiveUsage(liveUsageRef.current);
      },
      onDone: (data) => {
        const assistantMessage = data.assistantMessage || {};
        // Built before endTurn clears the refs it reads.
        const finished = {
          role: 'assistant',
          text: streamingTextRef.current || getMessageText(assistantMessage),
          createdAt: assistantMessage.createdAt || moment().toISOString(),
          usage: assistantMessage.usage || liveUsageRef.current || null,
          stopped: assistantMessage.stopped === true,
        };
        endTurn();
        callbacksRef.current.onDone?.(finished);
        ws.close();
      },
      onError: (data) => {
        endTurn();
        callbacksRef.current.onError?.(data.message || 'Failed to get a response. Please try again.');
        ws.close();
      },
      onClose: () => callbacksRef.current.onClose?.(),
    });
    wsRef.current = ws;

    try {
      await ws.connect(message, sessionRn, newSessionOptions);
    } catch {
      endTurn();
      callbacksRef.current.onError?.('Authentication error. Please refresh and try again.');
    }
  }, [endTurn]);

  const stop = React.useCallback(async () => {
    const run = activeRunRef.current;
    if (!run || stopping) return;
    setStopping(true);
    try {
      await backend.agentChat.stopSession(run.sessionRn, run.runRn);
    } catch (e) {
      if (activeRunRef.current === run) {
        setStopping(false);
        callbacksRef.current.onError?.(e?.message || 'Failed to stop the agent. Please try again.');
      }
      return;
    }
    // Nothing to do when the done frame already ended the turn.
    if (activeRunRef.current !== run) return;
    // The stop has ended the turn; if its done frame does not come, show what the engine saved.
    stopFallbackTimerRef.current = setTimeout(() => {
      wsRef.current?.close();
      endTurn();
      callbacksRef.current.onStopFallback?.(run.sessionRn);
    }, STOP_FALLBACK_MS);
  }, [endTurn, stopping]);

  return {loading, streamingText, liveUsage, canStop: Boolean(activeRun) && !stopping, stopping, send, stop};
};

export default useAgentChatTurn;
